/**
 * Contract Definition service targeting the v5beta multi-tenant API.
 *
 * Endpoint pattern: /v5beta/participants/{participantContextId}/contractdefinitions[/...]
 *
 * The participant context ID is read from the `participant_context_id` claim
 * of the current access token via TokenClaimsService.
 *
 * Auth headers are added by AuthHttpInterceptor.
 */
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContractDefinitionInput, ContractDefinition, IdResponse, QuerySpec } from '../model';
import { TokenClaimsService } from '../../app/auth/token-claims.service';
import { CONNECTOR_MANAGEMENT_API } from '../../app/variables';
import { ManagementRouteService } from '../common/managementRoute.service';
import { MANAGEMENT_API_CONTEXT } from '../variables';

@Injectable({
  providedIn: 'root',
})
export class ContractDefinitionService {
  constructor(
    private readonly http: HttpClient,
    private readonly managementRoute: ManagementRouteService
  ) {}

  // ── Overloads kept for backward-compat ───────────────────────────────────────

  public createContractDefinition(input: ContractDefinitionInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<IdResponse>;
  public createContractDefinition(input: ContractDefinitionInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<IdResponse>>;
  public createContractDefinition(input: ContractDefinitionInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<IdResponse>>;
  public createContractDefinition(input: ContractDefinitionInput): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<IdResponse>(`${url}/contractdefinitions`, this.toJsonLdInput(input));
  }

  public deleteContractDefinition(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public deleteContractDefinition(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public deleteContractDefinition(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public deleteContractDefinition(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling deleteContractDefinition.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.delete(`${url}/contractdefinitions/${encodeURIComponent(id)}`);
  }

  public updateContractDefinition(input: ContractDefinitionInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public updateContractDefinition(input: ContractDefinitionInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public updateContractDefinition(input: ContractDefinitionInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public updateContractDefinition(input: ContractDefinitionInput): Observable<any> {
    if (!input['@id']) throw new Error('Required parameter @id was null or undefined when calling updateContractDefinition.');

    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.put(`${url}/contractdefinitions`, this.toJsonLdInput(input));
  }

  public getContractDefinition(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<ContractDefinition>;
  public getContractDefinition(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<ContractDefinition>>;
  public getContractDefinition(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<ContractDefinition>>;
  public getContractDefinition(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling getContractDefinition.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<any>(`${url}/contractdefinitions/${encodeURIComponent(id)}`).pipe(
      map(item => this.toContractDefinitionInstance(item))
    );
  }

  public queryAllContractDefinitions(querySpec?: QuerySpec, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<Array<ContractDefinition>>;
  public queryAllContractDefinitions(querySpec?: QuerySpec, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<Array<ContractDefinition>>>;
  public queryAllContractDefinitions(querySpec?: QuerySpec, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<Array<ContractDefinition>>>;
  public queryAllContractDefinitions(querySpec?: QuerySpec): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<any[]>(`${url}/contractdefinitions/request`, this.toJsonLdQuerySpec(querySpec)).pipe(
      map(items => items.map(item => this.toContractDefinitionInstance(item)))
    );
  }

  private toJsonLdInput(input: ContractDefinitionInput): Record<string, any> {
    const assetsSelector = (input.assetsSelector ?? []).map((c: any) => ({
      '@type': 'Criterion',
      ...c,
    }));
    return {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'ContractDefinition',
      ...input,
      assetsSelector,
    };
  }

  private toContractDefinitionInstance(raw: any): any {
    const withCriterionShim = (criterion: any) => ({
      ...criterion,
      mandatoryValue: (_prefix: string, name: string) => criterion?.[name] ?? '',
      operandLeft:  criterion['operandLeft']  ?? '',
      operator:     criterion['operator']     ?? '',
      operandRight: criterion['operandRight'] ?? '',
    });

    const assetsSelector = (raw['assetsSelector'] ?? []).map(withCriterionShim);

    return {
      ...raw,
      id:               raw['@id'] ?? raw['id'],
      '@id':            raw['@id'] ?? raw['id'],
      accessPolicyId:   raw['accessPolicyId']   ?? '',
      contractPolicyId: raw['contractPolicyId'] ?? '',
      assetsSelector,
    };
  }

  private toJsonLdQuerySpec(querySpec?: QuerySpec): Record<string, any> {
    const body: Record<string, any> = {
      '@context': ['https://w3id.org/edc/connector/management/v2'],
      '@type': 'QuerySpec',
      'offset': querySpec?.offset ?? 0,
      'limit':  querySpec?.limit  ?? 50,
    };
    if (querySpec?.sortOrder)              body['sortOrder']        = querySpec.sortOrder;
    if (querySpec?.sortField)              body['sortField']        = querySpec.sortField;
    if (querySpec?.filterExpression?.length) body['filterExpression'] = querySpec.filterExpression;
    return body;
  }
}
